// <pos-checkout/> — 結帳頁（櫃檯建單）
// see architecture.md#L280 (payment API)、L1050 (cart state)、L1074 (payment state)

import { cartStore, clearCart } from '../lib/state/cart.js';
import { reduce, CART_STATES } from '../lib/state-machine/cart.js';
import { createOrder, listStoreTables, createCheckout, createLinePayRequest } from '../lib/api.js';
import { getSupabase } from '../lib/supabase-client.js';

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}

class PosCheckout extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = CART_STATES.CHECKOUT;
    this.method = 'cash';
    this.tables = [];
    this.tableNumber = '';       // '' = 外帶（takeout）
    this.customTable = '';       // 若店家沒預建桌位，允許手動輸入
    this.storeId = null;         // 多店面需顯式帶 store_id（Codex P1）
    this.error = null;
  }

  connectedCallback() {
    this.render();
    this.loadTables();
    this.loadStoreId();
  }

  async loadStoreId() {
    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const payload = session.access_token.split('.')[1];
      const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
      const claims = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
      const tenantId = claims.tenant_id;
      if (!tenantId) return;
      const { data: store } = await supabase
        .from('stores')
        .select('id')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      this.storeId = store?.id || null;
    } catch (err) {
      console.warn('[checkout] loadStoreId failed', err);
    }
  }

  async loadTables() {
    try {
      const { tables } = await listStoreTables();
      this.tables = tables || [];
      // 若有預建桌位，預設選第一桌；否則走外帶
      if (this.tables.length > 0 && !this.tableNumber) {
        this.tableNumber = this.tables[0].number;
      }
      this.render();
    } catch (err) {
      // 降級：沒桌位就全走外帶或手動輸入
      console.warn('[checkout] listStoreTables failed', err);
      this.tables = [];
      this.render();
    }
  }

  transition(event, ctx = {}) {
    const next = reduce(this.state, event, ctx);
    if (next !== this.state) {
      this.state = next;
      this.render();
    }
  }

  async submit() {
    // 重入保護：SUBMITTING 中再點（按鈕 disabled 是 UI 層，這裡是邏輯層雙保險）
    if (this.state === CART_STATES.SUBMITTING) return;
    const items = cartStore.get();
    if (items.length === 0) return;

    this.error = null;
    // OUTBOX（離線失敗後）→ retry 事件；其餘走 submit。
    // 狀態機若拒絕 transition（state 不變）就不打 API，避免狀態機形同虛設。
    this.transition(this.state === CART_STATES.OUTBOX ? 'retry' : 'submit');
    if (this.state !== CART_STATES.SUBMITTING) return;

    // 決定 table_number：有選桌→選桌；否則若手動輸入有值→手動；都沒→外帶（null）
    const table_number = this.tableNumber
      || (this.customTable.trim() ? this.customTable.trim() : undefined);

    try {
      const { order } = await createOrder({
        storeId: this.storeId || undefined,  // 省略時後端只接受單店 tenant
        items: items.map((i) => ({
          menu_item_id: i.menu_item_id,
          qty: i.qty,
          modifier_selections: i.modifiers,
        })),
        tableNumber: table_number,
        // P0 fix 2026-04-23：原本留 null 讓「結帳後另走 payment flow」但 B1 hotfix
        // 的 RPC 把 null payment_method 當非現金強制要求 payments.status='success'，
        // 造成 cashier 面收現金後按「已付款」409。現金面收本就是台灣 POS 主流場景，
        // 此處直接帶 this.method（cashier 在 checkout 選的付款方式）解決 B1 guard。
        // ECPay/LINE Pay 場景 checkout 會走 payment flow 建 payments row，
        // 此時 payment_method 已設好，webhook 回來 paid 時仍走 success row 驗證路徑。
        paymentMethod: this.method,
      });
      this.transition('success');
      clearCart();
      this.dispatchEvent(new CustomEvent('order-placed', {
        bubbles: true, composed: true, detail: { order },
      }));

      // 電子支付：建單成功後啟動金流（整頁離開，webhook 驗章回來才算 paid）
      if (this.method === 'ecpay' || this.method === 'linepay') {
        await this.startPayment(order);
        return;
      }

      // 現金：回訂單清單等面收
      setTimeout(() => { location.hash = `#/orders`; }, 400);
    } catch (err) {
      const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
      this.error = err.message_zh || err.message || '送單失敗';
      this.transition('network_error', { online });
    }
  }

  /**
   * 啟動電子支付（訂單已建立、購物車已清空）。
   * 失敗不丟回 cart 流程 —— 訂單仍在（pending），顯示導引讓店員去訂單清單
   * 重試或改收現金，避免重複建單。
   */
  async startPayment(order) {
    try {
      if (this.method === 'linepay') {
        const { payment_url } = await createLinePayRequest(order.id);
        if (!payment_url?.web) throw new Error('LINE Pay 未回傳付款連結');
        location.href = payment_url.web;   // LINE Pay 頁（桌機顯示 QR 供客人掃）
        return;
      }
      // 綠界：用回傳的 form_data 整頁 POST 到綠界收銀台
      const { form_action, form_data } = await createCheckout(order.id, 'ecpay');
      if (!form_action || !form_data) throw new Error('綠界未回傳付款表單');
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = form_action;
      for (const [k, v] of Object.entries(form_data)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = k;
        input.value = String(v);
        form.appendChild(input);
      }
      document.body.appendChild(form);   // shadow DOM 內 submit 不可靠，掛到 body
      form.submit();
    } catch (err) {
      // B1 衝突（已有進行中付款 / 金額已變動）後端回的 message_zh 已含完整指引，
      // 不再加「改收現金或重試」尾巴（amount_mismatch 直接重試無用，需先取消）。
      const CONFLICT_CODES = new Set(['payment_in_progress', 'pending_amount_mismatch']);
      if (CONFLICT_CODES.has(err.code)) {
        this.error = err.message_zh || '此訂單已有進行中的付款，請至訂單清單處理。';
      } else {
        this.error = (err.message_zh || err.message || '金流啟動失敗')
          + '。訂單已建立，可至訂單清單改收現金或重試。';
      }
      this.state = CART_STATES.CHECKOUT;  // 離開 OK 態，讓錯誤可見、按鈕可用
      this.render();
    }
  }

  render() {
    const items = cartStore.get();
    // 台灣餐飲慣例：菜單定價內含營業稅，結帳顯示多少客人就付多少。
    // 營業稅於月底依總營業額申報（不在單筆訂單上拆帳）。
    // 後端 tenants.tax_rate 預設 0、orders.tax 恆為 0（不影響 total）。
    const total = items.reduce((s, i) => s + i.price * i.qty, 0);

    const submitting = this.state === CART_STATES.SUBMITTING;
    const ok = this.state === CART_STATES.OK;
    const outbox = this.state === CART_STATES.OUTBOX;

    const hasTables = this.tables.length > 0;
    const isTakeout = !this.tableNumber && !this.customTable;

    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; padding: var(--s-5); max-width: 640px; margin: 0 auto; font-family: var(--font-sans); color: var(--fg-base); }
        h2 { font-size: 1.1rem; margin: 0 0 var(--s-3); color: var(--fg-base); }
        .section { margin-bottom: var(--s-5); }

        .tables { display: flex; flex-wrap: wrap; gap: var(--s-2); }
        .tbl-btn {
          padding: var(--s-2) var(--s-4); min-height: 44px; min-width: 64px;
          background: var(--bg-elevated); color: var(--fg-base);
          border: 2px solid var(--border); border-radius: var(--r-md);
          font-size: .95rem; cursor: pointer; font-family: inherit;
          display: flex; flex-direction: column; align-items: center; line-height: 1.2;
        }
        .tbl-btn:hover { border-color: var(--border-strong); }
        .tbl-btn[data-selected="true"] { border-color: var(--brand-primary); background: rgba(232,88,12,.08); color: var(--brand-primary); font-weight: 600; }
        .tbl-btn__name { font-size: .7rem; color: var(--fg-dim); margin-top: 2px; }
        .tbl-takeout { border-style: dashed; }

        .custom-row { margin-top: var(--s-3); display: flex; gap: var(--s-2); align-items: center; font-size: .85rem; color: var(--fg-muted); }
        .custom-row input { flex: 1; padding: var(--s-2) var(--s-3); background: var(--bg-base); color: var(--fg-base); border: 1px solid var(--border); border-radius: var(--r-sm); font-family: inherit; font-size: .9rem; }
        .custom-row input:focus { outline: none; border-color: var(--brand-primary); }

        .method { display: flex; gap: var(--s-3); }
        .method button {
          flex: 1; min-height: var(--touch-cta); padding: var(--s-4);
          background: var(--bg-elevated); border: 2px solid var(--border); color: var(--fg-base);
          border-radius: var(--r-lg); font-size: 16px; cursor: pointer; font-family: inherit;
        }
        .method button[data-selected="true"] {
          border-color: var(--brand-primary); background: rgba(232,88,12,.08); color: var(--brand-primary);
        }
        .summary { padding: var(--s-4); background: var(--bg-elevated);
                   border-radius: var(--r-md); margin-bottom: var(--s-4); }
        .summary-row { display: flex; justify-content: space-between; padding: var(--s-2) 0; }
        .total { font-size: 20px; font-weight: 600; color: var(--brand-primary); }
        .pay-btn {
          width: 100%; min-height: var(--touch-cta);
          background: var(--brand-primary); color: var(--fg-on-brand);
          border-radius: var(--r-md); font-size: 18px; font-weight: 600;
          margin-top: var(--s-3); cursor: pointer; border: 0;
        }
        .pay-btn[disabled] { opacity: .5; cursor: not-allowed; }
        .status { padding: var(--s-3); margin-top: var(--s-4); border-radius: var(--r-md); text-align: center; }
        .status-ok { background: var(--success-bg, #d4edda); color: var(--success-fg, #155724); }
        .status-err { background: var(--danger-bg, #f8d7da); color: var(--danger-fg, #721c24); }
        .status-outbox { background: var(--warning-bg, #fff3cd); color: var(--warning-fg, #856404); }
        .hint { color: var(--fg-dim); font-size: .8rem; margin-top: var(--s-2); }
      </style>

      <div class="section">
        <h2>桌號 / 內用外帶</h2>
        <div class="tables">
          <button class="tbl-btn tbl-takeout" data-tbl="" data-selected="${isTakeout}" data-testid="table-takeout">
            🥡 外帶
          </button>
          ${hasTables ? this.tables.map(t => `
            <button class="tbl-btn" data-tbl="${escapeHtml(t.number)}" data-selected="${this.tableNumber === t.number && !this.customTable}" data-testid="table-${escapeHtml(t.number)}">
              <span>桌 ${escapeHtml(t.number)}</span>
              ${t.name ? `<span class="tbl-btn__name">${escapeHtml(t.name)}</span>` : ''}
            </button>
          `).join('') : ''}
        </div>
        <div class="custom-row">
          <span>或手動：</span>
          <input id="customTable" type="text" maxlength="20" value="${escapeHtml(this.customTable)}" placeholder="臨時桌號（外帶留空）" />
        </div>
        ${!hasTables ? '<div class="hint">尚未建立桌位清單。建議到「設定 → 桌位 QR Code」先建立桌位。</div>' : ''}
      </div>

      <div class="section">
        <h2>結帳方式</h2>
        <div class="method" role="radiogroup" aria-label="付款方式">
          <button data-method="cash"    data-testid="method-cash"    data-selected="${this.method === 'cash'}">💵 現金</button>
          <button data-method="ecpay"   data-testid="method-ecpay"   data-selected="${this.method === 'ecpay'}">💳 綠界</button>
          <button data-method="linepay" data-testid="method-linepay" data-selected="${this.method === 'linepay'}">🟢 LINE Pay</button>
        </div>
      </div>

      <div class="summary" data-testid="checkout-summary">
        <div class="summary-row total"><span>總計</span><span data-testid="checkout-total">NT$ ${total}</span></div>
      </div>

      <button class="pay-btn" data-testid="pay-btn" ${items.length === 0 || submitting || ok ? 'disabled' : ''}>
        ${submitting ? '送單中...' : ok ? '✓ 已送單' : '確認建單'}
      </button>
      ${ok ? `<div class="status status-ok" data-testid="checkout-ok">訂單已建立，${this.method === 'cash' ? '正在前往訂單清單...' : '正在前往付款頁...'}</div>` : ''}
      ${outbox ? '<div class="status status-outbox" data-testid="checkout-outbox">⚠️ 目前離線，訂單「尚未」送出。購物車已保留，請恢復網路後再按一次「確認建單」。</div>' : ''}
      ${this.error && !outbox && !ok ? `<div class="status status-err" data-testid="checkout-err">${escapeHtml(this.error)}</div>` : ''}
      <p class="hint">現金：建單後至訂單管理頁按「✓ 已收款」。綠界 / LINE Pay：建單後自動前往付款頁，付款完成由系統驗章後自動標記已付款。</p>
    `;

    this.wireEvents();
  }

  wireEvents() {
    const root = this.shadowRoot;
    root.querySelectorAll('[data-tbl]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.tableNumber = btn.dataset.tbl;
        this.customTable = '';
        this.render();
      });
    });
    const custom = root.querySelector('#customTable');
    if (custom) custom.addEventListener('input', (e) => {
      this.customTable = e.target.value;
      if (this.customTable) this.tableNumber = '';
      // 不每字 render 以免失焦；只更新 selected state
      root.querySelectorAll('[data-tbl]').forEach(b => {
        b.dataset.selected = (b.dataset.tbl === '' && !this.customTable) ? 'true' : 'false';
      });
    });
    root.querySelectorAll('[data-method]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.method = btn.dataset.method;
        this.render();
      });
    });
    root.querySelector('.pay-btn')?.addEventListener('click', () => this.submit());
  }
}

customElements.define('pos-checkout', PosCheckout);
